import { BoxGeometry, BufferGeometry, Vector3 } from 'three';
import type { Matrix4, BufferAttribute } from 'three';

import type { STOCK_TYPES } from '../constants';
import type { OptimalBoundingBoxJson } from '../contracts/part';
import { disposeThreeJS, norm } from '../geom-utils';
import type { ResultForCreation } from '../renderer/stock-utils';
import { getBaseRectangularTransform } from '../renderer/stock-utils';

type OptimalBoundingBoxData = {
  length: number;
  width: number;
  height: number;
  center: Vector3;
  lengthDirection: Vector3;
  widthDirection: Vector3;
  heightDirection: Vector3;
};

export class OptimalBoundingBoxGeometry {
  static fromRawData({
    length,
    width,
    height,
    center,
    lengthDirection,
    widthDirection,
    heightDirection,
  }: OptimalBoundingBoxJson): OptimalBoundingBoxGeometry {
    return new OptimalBoundingBoxGeometry({
      length,
      width,
      height,
      center: new Vector3(center.x, center.y, center.z),
      lengthDirection: new Vector3(
        lengthDirection.x,
        lengthDirection.y,
        lengthDirection.z,
      ),
      widthDirection: new Vector3(
        widthDirection.x,
        widthDirection.y,
        widthDirection.z,
      ),
      heightDirection: new Vector3(
        heightDirection.x,
        heightDirection.y,
        heightDirection.z,
      ),
    });
  }

  public readonly box: BoxGeometry;
  public readonly length: number;
  public readonly width: number;
  public readonly height: number;
  public readonly center: Vector3;
  public readonly lengthDirection: Vector3;
  public readonly widthDirection: Vector3;
  public readonly heightDirection: Vector3;
  public readonly geometry: BufferGeometry;
  public readonly min: Vector3;
  public readonly max: Vector3;
  public readonly minSideArea: number;
  public readonly maxSideArea: number;
  public readonly diagonal: number;
  public readonly volume: number;
  private isDisposed?: boolean;

  constructor({
    length,
    width,
    height,
    center,
    lengthDirection,
    widthDirection,
    heightDirection,
  }: OptimalBoundingBoxData) {
    this.length = length;
    this.width = width;
    this.height = height;
    this.center = center;
    this.lengthDirection = lengthDirection;
    this.widthDirection = widthDirection;
    this.heightDirection = heightDirection;

    //Create the geometry
    const transform: Matrix4 = getBaseRectangularTransform(
      {
        lengthDirection: this.lengthDirection,
        widthDirection: this.widthDirection,
        heightDirection: this.heightDirection,
        center: this.center,
      } as ResultForCreation<STOCK_TYPES.RECTANGULAR>,
      this.length,
    );

    this.box = new BoxGeometry(this.width, this.height, this.length);
    const position = this.box.attributes.position as BufferAttribute;
    const min = (this.min = new Vector3().fromBufferAttribute(position, 6));
    const max = (this.max = new Vector3().fromBufferAttribute(position, 0));

    // make pairs of vertices for line segments geometry
    const vertices = [
      // bottom lines
      new Vector3(min.x, min.y, min.z),
      new Vector3(max.x, min.y, min.z),
      new Vector3(min.x, min.y, min.z),
      new Vector3(min.x, max.y, min.z),
      new Vector3(min.x, max.y, min.z),
      new Vector3(max.x, max.y, min.z),
      new Vector3(max.x, min.y, min.z),
      new Vector3(max.x, max.y, min.z),
      // top lines
      new Vector3(min.x, min.y, max.z),
      new Vector3(max.x, min.y, max.z),
      new Vector3(min.x, min.y, max.z),
      new Vector3(min.x, max.y, max.z),
      new Vector3(min.x, max.y, max.z),
      new Vector3(max.x, max.y, max.z),
      new Vector3(max.x, min.y, max.z),
      new Vector3(max.x, max.y, max.z),
      // front lines
      new Vector3(min.x, min.y, min.z),
      new Vector3(min.x, min.y, max.z),
      new Vector3(max.x, min.y, min.z),
      new Vector3(max.x, min.y, max.z),
      // back lines
      new Vector3(min.x, max.y, min.z),
      new Vector3(min.x, max.y, max.z),
      new Vector3(max.x, max.y, min.z),
      new Vector3(max.x, max.y, max.z),
    ];

    //Would like to only applyMatrix4 once...
    //* Tried doing it on `this.box` and not elsewhere, it sort of works, but there are issues with aligning lines
    //  with box in OBBSimplifiedRepresentation.
    //* The reasons for splitting it out like this may have to do with keeping width, height, length aligned consistently
    //  and min/max needs the consistency.
    //* Not sure why BoxHelper is not used. (Certainly room to optimize this, but performance is not an issue so leaving as is)

    this.geometry = new BufferGeometry()
      .setFromPoints(vertices)
      .applyMatrix4(transform);
    this.min.applyMatrix4(transform);
    this.max.applyMatrix4(transform);
    this.box.applyMatrix4(transform);

    [this.minSideArea, , this.maxSideArea] = [
      length * width,
      width * height,
      length * height,
    ]
      .map((v) => {
        const p = Math.abs(v);
        return Number.isFinite(p) ? p : 0;
      })
      .sort();

    this.diagonal = norm([length || 0, width || 0, height || 0]);

    this.volume = Math.abs(length * width * height);
  }

  dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    disposeThreeJS(this.box, this.geometry);
  }
}
